import MarkdownIt from "markdown-it";
import sanitizeHtml, { IOptions } from "sanitize-html";
import { metadata_block } from "./MarkdownMetaPlugin";
import { markdownCheckboxPlugin } from "./MarkdownCheckboxPlugin";
import { EL } from "./DomUtils";
import { signal } from "../lib/signal";
import {attrListToKv, csvToArray, shallowClone, splitEscapedKv} from "./CoreUtils";
import {custom_ctrl_seq} from "./MarkdownCustomEmoji";

export class Markdown {
    public disableSyntaxHighlight : boolean = false;
    private codeBlocks : any = null;
    private codeBlockRenderers = signal<(str: string, language: string, attrs: string) => string | undefined>();
    private renderer : MarkdownIt;
    private sanitizeHtml_validColors = [/^[a-z]+$/i, /^#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/, /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([\d.]{1,3})\s*\)$/];
    private sanitizeHtml_validTextshadow = [/^(([a-z]+|#(0x)?[0-9a-f]+|rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)|rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([\d.]{1,3})\s*\))\s+-?[0-9.]+(?:px|em|%)?\s+-?[0-9.]+(?:px|em|%)?\s+-?[0-9.]+(?:px|em|%)?\s*,?\s*)*$/];
    private sanitizeHtml_validImages = [/^[a-z-]+-gradient\(([^)]|rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)|rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([\d.]{1,3})\s*\))+\)$/];
    private sanitizeHtml_allowedNumbers = [/^-?[0-9.]+(?:px|em|%)?$/];
    private sanitizeHtml_allowedNumbersMulti = [/^(-?[0-9.]+(?:px|em|%)?\s+){0,3}-?[0-9.]+(?:px|em|%)?$/];
    public sanitizeHtmlOptions : IOptions = {
        disallowedTagsMode: 'escape',
        allowedTags: ['input', 'label', 'textarea', 'details', 'summary', 'miau-icon', 'miau-emoji'].concat(sanitizeHtml.defaults.allowedTags),
        allowedSchemes: ['meow'].concat(sanitizeHtml.defaults.allowedSchemes),
        allowedAttributes: Object.assign({}, sanitizeHtml.defaults.allowedAttributes, {
            '*': ['style', 'aria-hidden', 'title'],
            'annotation': ['encoding'],
            'math': ['xmlns'],
            'input': ['type', 'checked', 'disabled', 'id', 'name', 'value'],
            'textarea': ['disabled', 'id', 'name'],
            'label': ['for'],
            'miau-icon': ['src', 'size'],
            'miau-emoji': ['src'],
            'ol': ['start'],
        }),
        allowedClasses: {
            '*': [ '*' ]
        },
        allowedStyles: {
          '*': {
            // Match HEX and RGB
            'color': this.sanitizeHtml_validColors,
            'background-color': this.sanitizeHtml_validColors,
            'border-color': this.sanitizeHtml_validColors,
            'background-image': this.sanitizeHtml_validImages,
            'font-weight': [/^[a-z]+$/],
            'text-decoration': [/^[a-z ]+$/],
            'font-family': [/^[a-zA-Z '",-]+/],
            'text-align': [/^left$/, /^right$/, /^center$/],
            // Match any number with px, em, or %
            'font-size': this.sanitizeHtml_allowedNumbers,
            'margin-top': this.sanitizeHtml_allowedNumbers,
            'margin-left': this.sanitizeHtml_allowedNumbers,
            'margin-right': this.sanitizeHtml_allowedNumbers,
            'margin-bottom': this.sanitizeHtml_allowedNumbers,
            'padding-top': this.sanitizeHtml_allowedNumbers,
            'padding-left': this.sanitizeHtml_allowedNumbers,
            'padding-right': this.sanitizeHtml_allowedNumbers,
            'padding-bottom': this.sanitizeHtml_allowedNumbers,
            'margin': this.sanitizeHtml_allowedNumbersMulti,
            'padding': this.sanitizeHtml_allowedNumbersMulti,
            'top': this.sanitizeHtml_allowedNumbers,
            'left': this.sanitizeHtml_allowedNumbers,
            'width': this.sanitizeHtml_allowedNumbers,
            'height': this.sanitizeHtml_allowedNumbers,
            'vertical-align': this.sanitizeHtml_allowedNumbers,
            'border-width': this.sanitizeHtml_allowedNumbers,
            'border-top-width': this.sanitizeHtml_allowedNumbers,
            'border-left-width': this.sanitizeHtml_allowedNumbers,
            'border-right-width': this.sanitizeHtml_allowedNumbers,
            'border-bottom-width': this.sanitizeHtml_allowedNumbers,
            'text-shadow': this.sanitizeHtml_validTextshadow,
          },
        },
    };

    constructor() {
        this.renderer = MarkdownIt({
            breaks: true,
            linkify: true,
            html: true,
            typographer: true,
        });
        this.renderer.use(metadata_block, {
            parseMetadata: Markdown.parseMetadata,
        });
        this.renderer.use(markdownCheckboxPlugin, {
            disabled: false,
        });
        this.renderer.use(custom_ctrl_seq, {
        });
        this.renderer.options.highlight = (str, language, attrs)=>{
            if (this.codeBlocks) this.codeBlocks.push({code: str, lang: language, attrs});
            if (this.disableSyntaxHighlight) return;
            return this.codeBlockRenderers.collectFirst(str, language, attrs);
        };
        this.codeBlockRenderers.on(function(str, language, attrs) {
            if (language === 'csv') {
                let opts = splitEscapedKv(attrs);
                if (!('fSep' in opts)) opts.fSep = "\t";
                if (!('head' in opts)) opts.head = "true";
                return Markdown.csvToHtml(str, opts);
            }
        });
    }

    static csvToHtml(str, opts) {
        let header = false;
        opts = shallowClone(opts);
        if (opts?.head && opts?.head !== 'false') header = true;
        opts.head = false;
        const array = csvToArray(str, opts);
        const e = EL('table', {},
            ...array.map((row, ri) => EL('tr', {}, ...row.map(cell => EL((ri === 0 && header) ? 'th' : 'td', {}, cell))))
        );
        console.log(e)
        return e.outerHTML;
    }
    static csvToArray(str, opts) {
        return csvToArray(str, opts)
    }

    static parseMetadata(str: string): any {
        const re = /^([a-zA-Z][a-zA-Z0-9_]*):\s+(.*)$/;
        const meta = {};
        for(const line of str.split("\n")) {
            const m = line.match(re);
            if (!m) return false;
            meta[m[1]] = m[2];
        }
        return meta;
    }

    render(md: string, inline: boolean, meta?: any, codeBlocks?: any): string {
        this.codeBlocks = codeBlocks;
        var str = inline ? this.renderer.renderInline(md, { meta }) : this.renderer.render(md, { meta });
        this.codeBlocks = null;
        return sanitizeHtml(str, this.sanitizeHtmlOptions);
    }

    sanitize(html: string): string {
        return sanitizeHtml(html, this.sanitizeHtmlOptions);
    }

    loadPlugins() {
        import('../cm/syntaxHighlightUtil').then(util => {
            this.codeBlockRenderers.on(function(str, language, attrs) {
                const result = util.renderString(str, { language });
                if (result)
                    return result.code;
            });
            for (const language in util.supportedLanguages) {
                var items = document.querySelectorAll(`pre code.language-${language}`);
                for (let i = 0; i < items.length; i++) {
                    const item = items[i] as HTMLElement;
                    const result = util.renderString(item.innerText, { language });
                    if (result) {
                        const node = EL('div', {});
                        node.innerHTML = result.code;
                        (item.parentNode as HTMLElement).replaceWith(node.firstChild);
                    }
                }
            }
        });
        import("@traptitech/markdown-it-katex").then(katex => {
            this.renderer.use(katex.default, {output:"html"});
        });
    }
}
